import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { TbCalendarDollar } from 'react-icons/tb';
import { useApiGateway } from 'hooks/useApi';
import { useEarningsData } from 'hooks/useEarningsQuery';
import { useEarnings } from 'hooks/useEarnings';
import { getEarningsTooltip } from 'utils/shared/earnings';
import TooltipComponent from 'components/Tooltip';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { CompanyProfile, CompanyQuote, emptyProfile, emptyQuote } from 'constants/stockApi';
import Card from 'components/Card';
import LivePriceValue from 'components/LivePriceValue';
import useLiveSpot from 'hooks/useLiveSpot';

interface TickerDetails {
  profile: CompanyProfile;
  quote: CompanyQuote;
}

interface DisplayedDetails {
  name: string;
  marketCap: number;
  price: number;
  isEtf: boolean;
  yearLow: number;
  yearHigh: number;
  dayLow: number;
  dayHigh: number;
  priceAvg50: number;
  priceAvg200: number;
  range: string;
  earningsAnnouncement: string;
  open: number;
  sector?: string | null;
  logo?: string;
}

export const isInPast = (earningsDateString: string) => {
  if (!earningsDateString) {
    return true;
  }

  const today = new Date();
  const earningsDate = new Date(earningsDateString);

  if (today.getFullYear() > earningsDate.getFullYear()) {
    return true;
  }

  if (
    today.getFullYear() === earningsDate.getFullYear() &&
    today.getMonth() > earningsDate.getMonth()
  ) {
    return true;
  }

  if (
    today.getFullYear() === earningsDate.getFullYear() &&
    today.getMonth() === earningsDate.getMonth() &&
    today.getDate() > earningsDate.getDate()
  ) {
    return true;
  }

  return false;
};

const defaultTickerDetails = {
  profile: emptyProfile,
  quote: emptyQuote,
};

const TickerDetailsCard: React.FC<
  React.PropsWithChildren<{
    activeTicker: string;
    sector?: string | null;
    setSelectedTickerPrice?: React.Dispatch<React.SetStateAction<number>>;
    refreshTrigger?: number;
    isHideBottomRow?: boolean;
    className?: string;
    overridePrice?: number;
    isPriceLive?: boolean;
  }>
> = ({
  activeTicker,
  sector,
  setSelectedTickerPrice,
  refreshTrigger,
  isHideBottomRow = false,
  className = '',
  overridePrice,
  isPriceLive = false,
}) => {
  /*
      THE LIVE PRICE LIVES HERE, NOT AT THE CALL SITES.

      This card is mounted on Ticker Research, the OPC strategy screen and Gamma Exposure. Only the
      GEX mount ever passed a moving price in; the other two rendered a single snapshot fetched once
      per ticker and then frozen for the life of the page, in every session. Subscribing here fixes
      all three at once and keeps them in step.

      `overridePrice` still wins where a caller has a better number (GEX prefers the spot that
      drives its own gamma maths), so this is additive rather than a change of source.
    */
  const liveSpot = useLiveSpot(activeTicker);
  const hasOverride =
    typeof overridePrice === 'number' && Number.isFinite(overridePrice) && overridePrice > 0;
  const [tickerDetails, setTickerDetails] = React.useState<TickerDetails>(defaultTickerDetails);
  const { get, isLoading } = useApiGateway<never, TickerDetails>();

  const details: DisplayedDetails = {
    name: tickerDetails.profile.companyName || tickerDetails.quote.name || '',
    marketCap: tickerDetails.profile.mktCap || tickerDetails.quote.marketCap || 0,
    price:
      (hasOverride ? overridePrice : null) ||
      liveSpot.price ||
      tickerDetails.quote.price ||
      tickerDetails.profile.price ||
      0,
    isEtf: tickerDetails.profile.isEtf,
    yearLow: tickerDetails.quote.yearLow,
    yearHigh: tickerDetails.quote.yearHigh,
    dayLow: tickerDetails.quote.dayLow,
    dayHigh: tickerDetails.quote.dayHigh,
    range: tickerDetails.profile.range,
    earningsAnnouncement: tickerDetails.quote.earningsAnnouncement,
    open: tickerDetails.quote.open,
    priceAvg50: tickerDetails.quote.priceAvg50 || 0,
    priceAvg200: tickerDetails.quote.priceAvg200 || 0,
    sector,
    logo: tickerDetails.profile.image,
  };
  const { earningsMaps, fetchEarnings } = useEarnings();
  const { hasEarnings, getEarningsData, getDaysToEarnings } = useEarningsData(
    earningsMaps.earnings,
  );

  const tickerHasEarnings = hasEarnings(activeTicker);
  const earningsData = getEarningsData(activeTicker);
  const daysToEarnings = getDaysToEarnings(activeTicker);

  const tooltipTitle = tickerHasEarnings
    ? getEarningsTooltip(earningsData, daysToEarnings)
    : 'No upcoming earnings';

  const iconColor = tickerHasEarnings ? 'text-graph-purple' : 'text-gray-400';

  React.useEffect(() => {
    if (activeTicker) {
      get({ endpoint: `v1/tickers/details/${activeTicker}` })
        .then(({ data, isError }) => {
          if (!isError && data && typeof data !== 'string') {
            if (
              activeTicker === data.profile?.symbol.replace('^', '') ||
              activeTicker === data.quote?.symbol.replace('^', '')
            ) {
              setTickerDetails({
                profile: data.profile || emptyProfile,
                quote: data.quote || emptyQuote,
              });
              if (setSelectedTickerPrice) {
                setSelectedTickerPrice(data.quote?.price || data.profile?.price || 0);
              }
            }
          }
        })
        .catch((err) => {
          Sentry.withScope((_scope) => {
            Sentry.captureException(err);
          });
          setTickerDetails(defaultTickerDetails);
        });
    }
  }, [activeTicker, refreshTrigger]);

  React.useEffect(() => {
    if (activeTicker) {
      fetchEarnings();
    }
  }, [activeTicker, fetchEarnings]);

  return (
    <Card
      className={
        className ? className : 'ml-2 hidden w-full items-center justify-between md:flex md:w-1/3'
      }
    >
      {!!details.name && !isLoading ? (
        <div className="w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <TooltipComponent title={tooltipTitle} position="top">
                <TbCalendarDollar
                  aria-hidden="true"
                  className={`h-4 w-4 ${iconColor} cursor-pointer`}
                />
              </TooltipComponent>
              <h3 className="text-xs font-semibold">
                {details.name}{' '}
                {!!sector && <span className="text-[9px] font-normal text-gray-400">{sector}</span>}
              </h3>
            </div>
            {details?.logo && (
              <img
                src={details?.logo}
                className="h-6 w-6 rounded-full"
                alt={details.name ? `${details.name} logo` : 'Logo'}
              />
            )}
          </div>
          {/* <div className="flex">
            <p className="text-xs font-light">
              <span className="text-gray-400">Earnings Date:</span>{' '}
              {!details.earningsAnnouncement || isInPast(details.earningsAnnouncement)
                ? '1 month or more'
                : new Date(details.earningsAnnouncement).toLocaleDateString()}
            </p>
          </div> */}
          <div className="flex">
            <p className="w-1/2 text-xs font-light">
              <span className="text-gray-400">Price:</span>{' '}
              <LivePriceValue
                value={details.price}
                live={hasOverride ? isPriceLive : liveSpot.isLive}
                sessionPhase={liveSpot.phase ?? 'regular'}
                className="text-white"
                dotSize={4}
                dotRight={-5}
                dotTop={0}
              />
            </p>
            <p className="w-1/2 text-xs font-light">
              <span className="text-gray-400">Mkt Cap:</span> $
              {abbreviateNumber(details.marketCap, 1)}
            </p>
          </div>
          <div className="flex">
            <p className="w-1/2 text-xs font-light">
              <span className="text-gray-400">Day Low:</span> ${details.dayLow.toFixed(2)}
            </p>
            <p className="w-1/2 text-xs font-light">
              <span className="text-gray-400">Day High:</span> ${details.dayHigh.toFixed(2)}
            </p>
          </div>
          <div className="flex">
            <p className="w-1/2 text-xs font-light">
              <span className="text-gray-400">Year Low:</span> ${details.yearLow.toFixed(2)}
            </p>
            <p className="w-1/2 text-xs font-light">
              <span className="text-gray-400">Year High:</span> ${details.yearHigh.toFixed(2)}
            </p>
          </div>
          {!isHideBottomRow && (
            <div className="flex">
              <p className="w-1/2 text-xs font-light">
                <span className="text-gray-400">50-Day MA:</span> ${details.priceAvg50.toFixed(2)}
              </p>
              <p className="w-1/2 text-xs font-light">
                <span className="text-gray-400">200-Day MA:</span> ${details.priceAvg200.toFixed(2)}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div></div>
      )}
    </Card>
  );
};

export default TickerDetailsCard;
